import { getBaseUrl } from 'shared/utils/sse';
import { makeLog } from 'shared/utils/log';
import { message } from 'antd';
import { updateOrAddDialSessionTask } from 'shared/store/livelisten-slice';
import { useDispatch } from 'react-redux';
import { DialSession } from 'types/outbound';
import { OutboundSessionStates } from 'browser/components/Outbound/OutboundSession';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAllRooms } from 'shared/store/salesfloor-slice';

const log = makeLog('LiveListenDialSessionStatusUpdates');

export const DialSessionStatusUpdates = ({
  dialSession,
}: {
  dialSession: DialSession;
}) => {
  const eventSource = useRef<EventSource | null>(null);
  const [status, setStatus] = useState<null | OutboundSessionStates>(null);
  const dispatch = useDispatch();

  // Listen to DialTask updates via SSE
  const setupSSE = useCallback(
    async (sessionId: number): Promise<void> => {
      const SSE_ENDPOINT = `${getBaseUrl()}/api/v1/outbound/dial_session/stream`;

      const formatError = (error: any): string => {
        if (typeof error === 'string') {
          return error;
        } else if (error.message !== undefined) {
          return error.message;
        } else {
          return error.toString();
        }
      };

      const reconnectSSE = (
        sessionId: number,
        toStatus: OutboundSessionStates | null,
        retries = 300,
      ): Promise<void> | void | undefined => {
        const fromStatus = status;
        log.debug('Reconnecting SSE.', { sessionId, fromStatus, toStatus, retries });
        if (fromStatus !== OutboundSessionStates.reconnecting) {
          log.debug('Cannot reconnect SSE.', {
            sessionId,
            fromStatus,
            toStatus,
            retries,
          });
          return;
        }
        const retry = (error: any) => {
          if (retries > 0) {
            setTimeout(() => reconnectSSE(sessionId, toStatus, retries - 1), 1000);
          } else {
            message.error(`Failed to reconnect: ${formatError(error)}`);
          }
        };

        try {
          eventSource.current?.close();
          eventSource.current = null;
          setupSSE(sessionId);
        } catch (error) {
          log.error('Failed to reconnect SSE.', { sessionId, error: formatError(error) });
          return retry(error);
        }
      };

      log.assert(eventSource.current === null, 'eventSource already exists');
      return new Promise((resolve, reject) => {
        let open = false;
        const channel = `session.${sessionId}`;
        const eventSourceUrl = `${SSE_ENDPOINT}?channel=${channel}`;
        eventSource.current = new EventSource(eventSourceUrl);
        eventSource.current.onopen = () => {
          open = true;
          resolve();
          log.debug('SSE open', channel);
        };

        eventSource.current.onerror = error => {
          if (!open) reject(error);
          log.error('SSE failed', { error: formatError(error), channel });

          if (status !== OutboundSessionStates.reconnecting) {
            message.warn('Connection lost. Reconnecting...');
            setStatus(OutboundSessionStates.reconnecting);
            reconnectSSE(sessionId, status);
          }
        };

        eventSource.current.onmessage = ({ data }) => {
          const message = JSON.parse(data);

          if (message.heartbeat) {
            return;
          } else if (!message.handshake) {
            log.debug('Message:', message);
            dispatch(
              updateOrAddDialSessionTask({
                dialSessionId: dialSession.id,
                dialTask: message,
              }),
            );
            dispatch(fetchAllRooms());
          }
        };
      });
    },
    [dispatch, dialSession.id, status],
  );

  useEffect(() => {
    log.info(`Setting up SSE for dial session: ${dialSession.id}`);
    setupSSE(dialSession.id);

    return () => {
      eventSource.current?.close();
      eventSource.current = null;
    };
  }, [dialSession.id, setupSSE]);

  return null;
};
